import { CircleAlert, CircleCheck, Clock, Download, Monitor, ShieldAlert } from 'lucide-react'
import { Field, NumberInput, SwitchRow } from './form.js'
import { quandRelatif } from './backup-lisible.js'

/**
 * « PC DU BUREAU » — la copie hors du serveur (lot C).
 *
 * Le PC fixe du bureau vient chercher lui-même, chaque heure, la dernière
 * sauvegarde de Gestion. C'est lui qui tire : le serveur ne peut ni effacer
 * ni modifier ce que le PC a déjà rangé.
 *
 * Cette carte n'a PAS sa propre Save Bar : ses trois réglages appartiennent
 * au même réglage `backup` que la carte « Réglages », et passent par le même
 * enregistrement (voir BackupSettingsPanel). Elle reçoit donc sa saisie et
 * la rend par `onChange`.
 */

export interface EtatCopiePc {
  enabled: boolean
  keep: number
  alert_after_days: number
  ok: boolean
  /** Allumée, pas encore de passage, pas encore en retard : ni vert, ni alarme. */
  waiting: boolean
  phrase: string
  problems: string[]
  disk: boolean
  last_passage: { at: string; status: 'ok' | 'erreur'; error: string | null } | null
  last_success: { at: string; lot: string | null } | null
}

export interface SaisiePc { enabled: boolean; keep: string; alert: string }

export function PcBureauCard(props: {
  saisie: SaisiePc
  onChange: (p: Partial<SaisiePc>) => void
  etat: EtatCopiePc
  bornes: { keep: { min: number; max: number }; alert: { min: number; max: number } }
  /** Ce que la saisie de CETTE carte baisse (même règle que le serveur). */
  baisses: string[]
}) {
  const e = props.etat
  const s = props.saisie
  // L'état décrit ce qui est ENREGISTRÉ : tant que l'interrupteur n'est pas
  // enregistré, la phrase reste celle du serveur.
  const classe = !e.enabled ? 'bk-state--wait' : !e.ok ? 'bk-state--warn' : e.waiting ? 'bk-state--wait' : 'bk-state--ok'
  const passage = e.last_passage
  return (
    <section className="lc-panel">
      <header className="lc-panel__head">
        <Monitor size={18} aria-hidden="true" />
        <h2 className="lc-panel__title">PC du bureau</h2>
      </header>
      <div className="lc-panel__body bk-settings">
        <div className="bk-group">
          <p className={`bk-state ${classe}`} role="status">
            {!e.enabled || e.waiting ? <Clock size={20} aria-hidden="true" />
              : !e.ok ? <CircleAlert size={20} aria-hidden="true" /> : <CircleCheck size={20} aria-hidden="true" />}
            <span>{e.phrase}</span>
          </p>
          {e.problems.length > 1 ? (
            <ul className="bk-problems">
              {e.problems.slice(1).map((p) => <li key={p}>{p}</li>)}
            </ul>
          ) : null}
          {e.enabled ? (
            <p className="bk-pc-line">
              {passage
                ? <>Dernier passage {quandRelatif(passage.at)}
                  {passage.status === 'erreur'
                    ? <> · <span className="lc-pill lc-pill--warning">échec : {passage.error ?? 'erreur'}</span></>
                    : null}</>
                : 'Aucun passage pour l’instant'}
              {' '}· garde {e.keep} copies
              {e.last_success?.lot ? <> · copie la plus récente sur le PC : {e.last_success.lot}</> : null}
            </p>
          ) : null}
        </div>

        <SwitchRow
          checked={s.enabled}
          onChange={(v) => props.onChange({ enabled: v })}
          title="Copie sur le PC du bureau"
          desc={s.enabled
            ? 'Le PC récupère chaque heure la dernière sauvegarde (sans la clé maître). Gestion prévient s’il ne vient plus.'
            : 'Éteinte : rien n’est proposé au PC, et aucune alarme ne le concerne. Allumez-la une fois le PC installé.'}
        />

        <div className="bk-fields">
          <Field label="Copies gardées sur le PC"
            helper={`De ${props.bornes.keep.min} à ${props.bornes.keep.max}. Le PC efface lui-même les plus anciennes, mais jamais une copie de moins de 7 jours (ou de ce nombre de jours s’il est plus grand), et il garde en plus une copie complète par semaine sur 4 semaines.`}>
            <NumberInput value={s.keep} onChange={(v) => props.onChange({ keep: v })} unit="copies"
              min={props.bornes.keep.min} max={props.bornes.keep.max} ariaLabel="Nombre de copies gardées sur le PC" />
          </Field>
          <Field label="Prévenir si le PC n’a rien récupéré depuis"
            helper="Le PC doit être allumé, session ouverte, sur le réseau du club.">
            <NumberInput value={s.alert} onChange={(v) => props.onChange({ alert: v })} unit="jours"
              min={props.bornes.alert.min} max={props.bornes.alert.max} ariaLabel="Jours sans copie avant alerte" />
          </Field>
        </div>

        {props.baisses.length > 0 ? (
          <div className="bk-callout bk-callout--warn" role="status">
            <ShieldAlert size={16} aria-hidden="true" />
            <div>
              <strong>Ces changements réduisent la protection</strong> — votre identité vous sera
              redemandée à l’enregistrement :
              <ul className="bk-callout__list">
                {props.baisses.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="bk-group">
          <h3 className="bk-group__title">Installer sur le PC</h3>
          <ol className="bk-howto">
            <li>
              Sur le PC du bureau, téléchargez l’installateur, décompressez-le, puis double-cliquez
              sur <strong>INSTALLER.cmd</strong>. Aucun droit d’administrateur n’est nécessaire.
            </li>
            <li>
              À la fin, une ligne commençant par <span className="bk-mono">ssh-ed25519</span> s’affiche :
              envoyez-la à l’administrateur du serveur. Elle ne contient aucun secret.
            </li>
            <li>
              Revenez ici, allumez « Copie sur le PC du bureau » et enregistrez. La première copie
              arrive dans l’heure.
            </li>
          </ol>
          <p className="bk-group__help">
            L’administrateur du serveur ajoute la clé du PC, limitée à la lecture des sauvegardes :
            elle ne permet ni d’ouvrir une session, ni d’écrire, ni d’effacer.
            La clé maître n’est pas copiée sur le PC : gardez-la dans votre gestionnaire de mots de passe.
          </p>
          <a className="lc-btn bk-add" href="/api/v1/system/backups/pc/installer.zip" download="copie-gestion-pc.zip">
            <Download size={14} aria-hidden="true" /> Télécharger l’installateur (.zip)
          </a>
        </div>
      </div>
    </section>
  )
}
